import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	ArrowLeft,
	Coffee,
	Plus,
	Minus,
	Trash2,
	ShoppingCart,
	Search,
	X,
	RefreshCw,
	ImageOff,
	Check,
	Banknote,
	CreditCard,
	Loader2,
	CheckCircle2,
	AlertTriangle,
} from "lucide-react";
import {
	getSavedReaderId,
	newClientSaleId,
	pos,
} from "../../services/posService";
import { ErrorBox, Modal, useCurrentSession } from "./posShared";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const formatCHF = (value) =>
	new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF" }).format(
		Number(value) || 0,
	);

export default function POSCheckout() {
	const [categories, setCategories] = useState([]);
	const [products, setProducts] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("all");
	const [search, setSearch] = useState("");
	const [cart, setCart] = useState([]);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [reloadKey, setReloadKey] = useState(0);
	const [serviceType, setServiceType] = useState("takeaway");
	const [payment, setPayment] = useState(null); // "cash" | "terminal"
	const [completedSale, setCompletedSale] = useState(null);
	const { loading: sessionLoading, session, testMode, vatRates, reload: reloadSession, error: sessionError } =
		useCurrentSession();

	useEffect(() => {
		const controller = new AbortController();
		async function loadProducts() {
			setLoading(true);
			setError("");
			try {
				const response = await fetch(`${API_URL}/api/pos/catalog`, {
					signal: controller.signal,
				});
				if (!response.ok) throw new Error(`Erreur API : ${response.status}`);
				const data = await response.json();
				if (controller.signal.aborted) return;
				setCategories(Array.isArray(data.categories) ? data.categories : []);
				setProducts(Array.isArray(data.products) ? data.products : []);
			} catch (err) {
				if (err.name !== "AbortError")
					setError(err.message || "Impossible de charger le catalogue.");
			} finally {
				if (!controller.signal.aborted) setLoading(false);
			}
		}
		loadProducts();
		return () => controller.abort();
	}, [reloadKey]);

	useEffect(() => {
		if (!selectedProduct) return undefined;
		const onKeyDown = (event) => {
			if (event.key === "Escape") setSelectedProduct(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [selectedProduct]);

	const filteredProducts = useMemo(() => {
		const query = search.trim().toLocaleLowerCase("fr");
		return products.filter((product) => {
			if (
				selectedCategory !== "all" &&
				product.category_id !== selectedCategory
			)
				return false;
			if (!query) return true;
			return [
				product.name,
				product.short_description,
				product.slug,
				...(product.variants || []).map((v) => v.name),
			]
				.filter(Boolean)
				.some((value) => value.toLocaleLowerCase("fr").includes(query));
		});
	}, [products, selectedCategory, search]);

	function addToCart(product, variant = null) {
		const id = variant ? `${product.id}-${variant.id}` : product.id;
		const price = Number(variant ? variant.price : product.price);
		const rawStock = variant ? variant.stock : product.stock;
		const stockLimit = rawStock == null ? null : Number(rawStock);
		if (!Number.isFinite(price) || price < 0) return;
		setCart((previous) => {
			const existing = previous.find((item) => item.id === id);
			if (existing) {
				if (stockLimit != null && existing.quantity >= stockLimit) return previous;
				return previous.map((item) =>
					item.id === id ? { ...item, quantity: item.quantity + 1 } : item,
				);
			}
			return [
				...previous,
				{
					id,
					productId: product.id,
					stock: stockLimit,
					variantId: variant?.id || null,
					name: product.name,
					variant: variant?.name || null,
					price,
					quantity: 1,
				},
			];
		});
		setSelectedProduct(null);
	}

	function updateQuantity(id, change) {
		setCart((previous) =>
			previous
				.map((item) => {
					if (item.id !== id) return item;
					const next = item.quantity + change;
					if (change > 0 && item.stock != null && next > item.stock) return item;
					return { ...item, quantity: next };
				})
				.filter((item) => item.quantity > 0),
		);
	}

	const totalCents = cart.reduce(
		(sum, item) => sum + Math.round(item.price * 100) * item.quantity,
		0,
	);
	const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
	const activeVariants = (selectedProduct?.variants || []).filter(
		(variant) =>
			variant.available && (variant.stock == null || Number(variant.stock) > 0),
	);

	return (
		<div className="min-h-screen bg-[#F7F3ED] text-[#34251D]">
			<header className="flex flex-wrap items-center justify-between gap-3 bg-[#34251D] px-4 py-4 text-white sm:px-6">
				<div className="flex items-center gap-3">
					<Coffee size={27} aria-hidden="true" />
					<div>
						<h1 className="text-xl font-bold">Léger's Coffee</h1>
						<p className="text-xs text-white/70">
							Point de vente{testMode ? " — mode test Stripe" : ""}
						</p>
					</div>
				</div>
				<Link
					to="/pos"
					className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm transition hover:bg-white/20"
				>
					<ArrowLeft size={17} aria-hidden="true" /> Tableau de bord
				</Link>
			</header>

			<div className="grid min-h-[calc(100vh-76px)] grid-cols-1 lg:grid-cols-[minmax(0,1fr)_350px] xl:grid-cols-[minmax(0,1fr)_390px]">
				<main className="min-w-0 p-4 sm:p-5">
					{!sessionLoading && !session ? (
						<div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
							<span>
								{sessionError
									? `Caisse indisponible : ${sessionError}`
									: "La caisse n'est pas ouverte. Ouvrez-la avec le fond de caisse avant d'encaisser."}
							</span>
							<Link to="/pos" className="rounded-lg bg-[#34251D] px-4 py-2 font-semibold text-white">
								Ouvrir la caisse
							</Link>
						</div>
					) : null}
					<div className="mb-4 flex flex-wrap items-center gap-3">
						<label className="relative min-w-[220px] flex-1">
							<Search
								size={19}
								className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8D8179]"
								aria-hidden="true"
							/>
							<input
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Rechercher un produit..."
								className="w-full rounded-xl border border-[#E6DED6] bg-white py-3 pl-10 pr-4 outline-none focus:border-[#34251D]"
							/>
						</label>
						<button
							type="button"
							onClick={() => setReloadKey((value) => value + 1)}
							className="flex items-center gap-2 rounded-xl border border-[#E6DED6] bg-white px-4 py-3 text-sm hover:bg-[#F0E8E0]"
							title="Actualiser les produits"
						>
							<RefreshCw size={17} aria-hidden="true" /> Actualiser
						</button>
					</div>

					<div className="mb-5 flex gap-2 overflow-x-auto pb-2">
						{[{ id: "all", name: "Tous" }, ...categories].map((category) => (
							<button
								type="button"
								key={category.id}
								onClick={() => setSelectedCategory(category.id)}
								aria-pressed={selectedCategory === category.id}
								className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium transition ${selectedCategory === category.id ? "bg-[#34251D] text-white" : "bg-white text-[#34251D] shadow-sm hover:bg-[#EFE6DD]"}`}
							>
								{category.name}
							</button>
						))}
					</div>

					{loading && (
						<p className="rounded-xl bg-white p-5 text-sm">
							Chargement des produits...
						</p>
					)}
					{error && (
						<div
							role="alert"
							className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
						>
							{error}
						</div>
					)}
					{!loading && !error && filteredProducts.length === 0 && (
						<p className="rounded-xl bg-white p-5 text-sm text-[#766B63]">
							Aucun produit trouvé.
						</p>
					)}

					{!loading && !error && (
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
							{filteredProducts.map((product) => {
								const variants = (product.variants || []).filter(
									(variant) =>
										variant.available &&
										(variant.stock == null || Number(variant.stock) > 0),
								);
								const canAdd = product.has_variants
									? variants.length > 0
									: product.stock == null || Number(product.stock) > 0;
								return (
									<article
										key={product.id}
										className="flex min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-[#EDE6DF]"
									>
										<div className="aspect-square w-full overflow-hidden bg-[#F3EEE8]">
											{product.image_url ? (
												<img
													src={product.image_url}
													alt={product.name}
													loading="lazy"
													className="h-full w-full object-contain"
												/>
											) : (
												<div className="flex h-full flex-col items-center justify-center gap-2 text-[#B4A79D]">
													<ImageOff
														size={30}
														strokeWidth={1.5}
														aria-hidden="true"
													/>
													<span className="text-xs">Sans image</span>
												</div>
											)}
										</div>
										<div className="flex flex-1 flex-col p-3">
											<h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-5 sm:text-base">
												{product.name}
											</h3>
											{product.has_variants && (
												<p className="mt-1 text-xs text-[#82766D]">
													{variants.length} variante
													{variants.length > 1 ? "s" : ""}
												</p>
											)}
											<div className="mt-auto pt-3">
												<button
													type="button"
													disabled={!canAdd}
													onClick={() =>
														product.has_variants
															? setSelectedProduct(product)
															: addToCart(product)
													}
													className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl bg-[#34251D] px-2 py-2 text-sm font-medium text-white transition hover:bg-[#4A3529] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
												>
													{canAdd ? (
														<Plus size={16} aria-hidden="true" />
													) : null}
													{canAdd
														? product.has_variants
															? "Choisir"
															: formatCHF(product.price)
														: "Indisponible"}
												</button>
											</div>
										</div>
									</article>
								);
							})}
						</div>
					)}
				</main>

				<aside className="flex flex-col border-t border-[#E9E0D7] bg-white lg:sticky lg:top-0 lg:h-screen lg:border-l lg:border-t-0">
					<div className="flex items-center justify-between border-b border-[#EFE9E3] p-5">
						<div className="flex items-center gap-3">
							<ShoppingCart size={23} aria-hidden="true" />
							<h2 className="text-xl font-bold">Panier</h2>
						</div>
						<span className="rounded-full bg-[#F3EEE8] px-3 py-1 text-sm font-semibold">
							{itemCount}
						</span>
					</div>
					<div className="min-h-[160px] flex-1 space-y-3 overflow-y-auto p-5">
						{cart.length === 0 ? (
							<div className="py-10 text-center text-sm text-[#81768B]">
								Aucun produit ajouté.
							</div>
						) : (
							cart.map((item) => (
								<div
									key={item.id}
									className="rounded-xl border border-[#EEE6DF] p-3"
								>
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="font-semibold">{item.name}</p>
											{item.variant && (
												<p className="text-sm text-[#81756D]">{item.variant}</p>
											)}
										</div>
										<button
											type="button"
											aria-label={`Supprimer ${item.name}`}
											onClick={() =>
												setCart((previous) =>
													previous.filter((p) => p.id !== item.id),
												)
											}
											className="rounded-lg p-2 text-red-600 hover:bg-red-50"
										>
											<Trash2 size={17} aria-hidden="true" />
										</button>
									</div>
									<div className="mt-3 flex items-center justify-between gap-2">
										<div className="flex items-center gap-2">
											<button
												type="button"
												aria-label={`Diminuer ${item.name}`}
												onClick={() => updateQuantity(item.id, -1)}
												className="rounded-lg border p-2 hover:bg-[#F7F3ED]"
											>
												<Minus size={16} />
											</button>
											<span className="min-w-[20px] text-center font-semibold">
												{item.quantity}
											</span>
											<button
												type="button"
												aria-label={`Augmenter ${item.name}`}
												onClick={() => updateQuantity(item.id, 1)}
												className="rounded-lg border p-2 hover:bg-[#F7F3ED]"
											>
												<Plus size={16} />
											</button>
										</div>
										<span className="font-semibold">
											{formatCHF(
												(Math.round(item.price * 100) * item.quantity) / 100,
											)}
										</span>
									</div>
								</div>
							))
						)}
					</div>
					<div className="border-t border-[#EFE9E3] p-5">
						<div className="mb-4 grid grid-cols-2 gap-1 rounded-xl bg-[#F3EEE8] p-1 text-sm font-semibold">
							{[
								["takeaway", "À emporter"],
								["dine_in", "Sur place"],
							].map(([value, label]) => (
								<button
									key={value}
									type="button"
									onClick={() => setServiceType(value)}
									aria-pressed={serviceType === value}
									className={`rounded-lg py-2 ${serviceType === value ? "bg-white shadow-sm" : "text-[#81756D]"}`}
								>
									{label}
								</button>
							))}
						</div>
						<div className="flex items-center justify-between gap-2 text-lg font-bold">
							<span>Total</span>
							<span>{formatCHF(totalCents / 100)}</span>
						</div>
						{vatRates[serviceType] ? (
							<p className="text-right text-xs text-gray-500">
								dont TVA {vatRates[serviceType]} % incluse
							</p>
						) : null}
						<div className="mt-4 grid grid-cols-2 gap-3">
							<button
								type="button"
								disabled={!session || cart.length === 0}
								onClick={() => setPayment("terminal")}
								className="flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl bg-[#34251D] p-3 font-semibold text-white transition hover:bg-[#4A3529] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
							>
								<CreditCard size={22} aria-hidden="true" /> Terminal
							</button>
							<button
								type="button"
								disabled={!session || cart.length === 0}
								onClick={() => setPayment("cash")}
								className="flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border-2 border-[#34251D] bg-white p-3 font-semibold text-[#34251D] transition hover:bg-[#F7F3ED] disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
							>
								<Banknote size={22} aria-hidden="true" /> Espèces
							</button>
						</div>
						{cart.length > 0 ? (
							<button
								type="button"
								onClick={() => setCart([])}
								className="mt-3 w-full text-sm text-[#81756D] hover:underline"
							>
								Vider le panier
							</button>
						) : null}
					</div>
				</aside>
			</div>

			{selectedProduct && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
					onMouseDown={(event) => {
						if (event.target === event.currentTarget) setSelectedProduct(null);
					}}
				>
					<div
						role="dialog"
						aria-modal="true"
						aria-labelledby="pos-variant-title"
						className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
					>
						<div className="flex items-start justify-between gap-3 border-b p-5">
							<div>
								<h2 id="pos-variant-title" className="text-xl font-bold">
									{selectedProduct.name}
								</h2>
								<p className="mt-1 text-sm text-[#81756D]">
									Choisissez une variante
								</p>
							</div>
							<button
								type="button"
								onClick={() => setSelectedProduct(null)}
								aria-label="Fermer"
								className="rounded-lg p-2 hover:bg-[#F7F3ED]"
							>
								<X size={20} />
							</button>
						</div>
						<div className="max-h-[65vh] space-y-2 overflow-y-auto p-5">
							{activeVariants.length === 0 ? (
								<p className="text-sm text-gray-500">
									Aucune variante disponible.
								</p>
							) : (
								activeVariants.map((variant) => (
									<button
										type="button"
										key={variant.id}
										onClick={() => addToCart(selectedProduct, variant)}
										className="flex min-h-[62px] w-full items-center justify-between gap-3 rounded-xl border border-[#E7DED6] p-3 text-left transition hover:border-[#34251D] hover:bg-[#F7F3ED]"
									>
										<div className="min-w-0">
											<p className="font-semibold">{variant.name}</p>
											{variant.description && (
												<p className="mt-1 text-xs text-[#81756D]">
													{variant.description}
												</p>
											)}
										</div>
										<div className="flex shrink-0 items-center gap-2 font-semibold">
											<span>{formatCHF(variant.price)}</span>
											<Check size={17} />
										</div>
									</button>
								))
							)}
						</div>
					</div>
				</div>
			)}

			{payment && session ? (
				<PaymentFlow
					method={payment}
					session={session}
					cart={cart}
					totalCents={totalCents}
					serviceType={serviceType}
					testMode={testMode}
					onCancel={() => setPayment(null)}
					onPaid={(sale) => {
						setPayment(null);
						setCompletedSale(sale);
						setCart([]);
						setReloadKey((value) => value + 1);
						reloadSession();
					}}
				/>
			) : null}

			{completedSale ? (
				<SaleDoneModal sale={completedSale} onClose={() => setCompletedSale(null)} />
			) : null}
		</div>
	);
}

// ─────────────────────────────────────────────────────────────
// Paiement : espèces ou terminal
// ─────────────────────────────────────────────────────────────

function PaymentFlow({ method, session, cart, totalCents, serviceType, testMode, onCancel, onPaid }) {
	// Un identifiant par tentative : un double clic ne crée jamais deux ventes.
	const [clientSaleId] = useState(() => newClientSaleId());
	const payload = useMemo(
		() => ({
			session_id: session.session_id,
			service_type: serviceType,
			client_sale_id: clientSaleId,
			items: cart.map((item) => ({
				product_id: item.productId,
				variant_id: item.variantId,
				quantity: item.quantity,
			})),
		}),
		[session.session_id, serviceType, clientSaleId, cart],
	);

	return method === "cash" ? (
		<CashPayment payload={payload} totalCents={totalCents} onCancel={onCancel} onPaid={onPaid} />
	) : (
		<TerminalPayment payload={payload} totalCents={totalCents} testMode={testMode} onCancel={onCancel} onPaid={onPaid} />
	);
}

function quickAmounts(totalCents) {
	const total = totalCents / 100;
	const values = new Set([total]);
	[5, 10, 20, 50, 100, 200].forEach((note) => {
		const rounded = Math.ceil(total / note) * note;
		if (rounded > total) values.add(rounded);
	});
	return Array.from(values).sort((a, b) => a - b).slice(0, 5);
}

function CashPayment({ payload, totalCents, onCancel, onPaid }) {
	const [received, setReceived] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const receivedCents = Math.round(Number(String(received).replace(",", ".")) * 100);
	const valid = Number.isFinite(receivedCents) && receivedCents >= totalCents;
	const changeCents = valid ? receivedCents - totalCents : 0;

	async function confirm(event) {
		event?.preventDefault();
		if (!valid) {
			setError("Le montant reçu est inférieur au total.");
			return;
		}
		setBusy(true);
		setError("");
		try {
			const { sale } = await pos.createSale({
				...payload,
				payment_method: "cash",
				cash_received: (receivedCents / 100).toFixed(2),
			});
			onPaid(sale);
		} catch (e) {
			setError(e.message);
			setBusy(false);
		}
	}

	return (
		<Modal title="Paiement en espèces" onClose={busy ? null : onCancel}>
			<form onSubmit={confirm} className="space-y-4">
				<div className="rounded-xl bg-[#F7F3ED] p-4 text-center">
					<p className="text-sm text-gray-500">À encaisser</p>
					<p className="text-4xl font-bold">{formatCHF(totalCents / 100)}</p>
				</div>
				<label className="block">
					<span className="mb-1 block text-sm font-medium">Montant reçu (CHF)</span>
					<input
						value={received}
						onChange={(e) => setReceived(e.target.value)}
						inputMode="decimal"
						autoFocus
						placeholder={(totalCents / 100).toFixed(2)}
						className="w-full rounded-xl border p-4 text-2xl font-semibold"
					/>
				</label>
				<div className="flex flex-wrap gap-2">
					{quickAmounts(totalCents).map((value, index) => (
						<button
							key={value}
							type="button"
							onClick={() => setReceived(value.toFixed(2))}
							className="rounded-xl border px-4 py-3 font-semibold hover:bg-[#F7F3ED]"
						>
							{index === 0 ? "Compte juste" : formatCHF(value)}
						</button>
					))}
				</div>
				<div className={`rounded-xl p-4 text-center ${valid ? "bg-emerald-50 text-emerald-900" : "bg-gray-50 text-gray-500"}`}>
					<p className="text-sm">Monnaie à rendre</p>
					<p className="text-3xl font-bold">{formatCHF(changeCents / 100)}</p>
				</div>
				<ErrorBox message={error} />
				<button
					type="submit"
					disabled={!valid || busy}
					className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#34251D] p-4 text-lg font-semibold text-white disabled:opacity-50"
				>
					{busy ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
					{busy ? "Enregistrement…" : "Valider le paiement"}
				</button>
			</form>
		</Modal>
	);
}

const TERMINAL_POLL_MS = 2000;

function TerminalPayment({ payload, totalCents, testMode, onCancel, onPaid }) {
	const readerId = getSavedReaderId();
	const [phase, setPhase] = useState(readerId ? "sending" : "no-reader");
	const [sale, setSale] = useState(null);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const started = useRef(false);

	const start = useCallback(async () => {
		setPhase("sending");
		setError("");
		try {
			const { sale: created } = await pos.createSale({
				...payload,
				payment_method: "terminal",
				reader_id: readerId,
			});
			setSale(created);
			setPhase(created.status === "paid" ? "paid" : "waiting");
			if (created.status === "paid") onPaid(created);
		} catch (e) {
			setError(e.message);
			setPhase("error");
		}
	}, [payload, readerId, onPaid]);

	useEffect(() => {
		if (!readerId || started.current) return;
		started.current = true;
		start();
	}, [readerId, start]);

	// Suivi du paiement jusqu'à la confirmation de Stripe.
	useEffect(() => {
		if (phase !== "waiting" || !sale) return undefined;
		let stopped = false;
		const id = setInterval(async () => {
			try {
				const { sale: fresh } = await pos.getSale(sale.id);
				if (stopped) return;
				setSale(fresh);
				if (fresh.status === "paid") {
					setPhase("paid");
					onPaid(fresh);
				} else if (fresh.status === "void") {
					setError(fresh.payment?.failure || "Le paiement a été refusé ou annulé.");
					setPhase("error");
				}
			} catch {
				// réseau instable : on réessaie au prochain tour
			}
		}, TERMINAL_POLL_MS);
		return () => {
			stopped = true;
			clearInterval(id);
		};
	}, [phase, sale, onPaid]);

	async function cancel() {
		if (!sale) return onCancel();
		setBusy(true);
		try {
			const { sale: fresh } = await pos.cancelSale(sale.id);
			if (fresh.status === "paid") onPaid(fresh);
			else onCancel();
		} catch (e) {
			setError(e.message);
			// La vente a pu être payée juste avant l'annulation.
			try {
				const { sale: fresh } = await pos.getSale(sale.id);
				if (fresh.status === "paid") onPaid(fresh);
			} catch {
				// ignoré
			}
		} finally {
			setBusy(false);
		}
	}

	async function simulate() {
		if (!sale) return;
		setBusy(true);
		try {
			await pos.simulateCard(sale.id);
		} catch (e) {
			setError(e.message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<Modal title="Paiement par terminal" onClose={phase === "waiting" || phase === "sending" ? null : onCancel}>
			<div className="space-y-4 text-center">
				<p className="text-4xl font-bold">{formatCHF(totalCents / 100)}</p>

				{phase === "no-reader" ? (
					<>
						<AlertTriangle size={40} className="mx-auto text-amber-600" />
						<p>Aucun terminal n'est sélectionné sur cet appareil.</p>
						<Link to="/pos" className="inline-block rounded-xl bg-[#34251D] px-5 py-3 font-semibold text-white">
							Choisir le terminal
						</Link>
					</>
				) : null}

				{phase === "sending" ? (
					<>
						<Loader2 size={40} className="mx-auto animate-spin text-[#34251D]" />
						<p>Envoi du montant au terminal…</p>
					</>
				) : null}

				{phase === "waiting" ? (
					<>
						<CreditCard size={44} className="mx-auto text-[#34251D]" />
						<p className="text-lg font-semibold">Le client présente sa carte ou son téléphone sur le terminal</p>
						<p className="flex items-center justify-center gap-2 text-sm text-gray-500">
							<Loader2 size={15} className="animate-spin" /> En attente de la confirmation de Stripe…
						</p>
						{testMode ? (
							<button
								type="button"
								onClick={simulate}
								disabled={busy}
								className="w-full rounded-xl border border-dashed border-amber-400 bg-amber-50 p-3 font-semibold text-amber-900 disabled:opacity-60"
							>
								Simuler le paiement par carte (mode test)
							</button>
						) : null}
						<button
							type="button"
							onClick={cancel}
							disabled={busy}
							className="w-full rounded-xl border p-3 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
						>
							Annuler le paiement
						</button>
					</>
				) : null}

				{phase === "error" ? (
					<>
						<AlertTriangle size={40} className="mx-auto text-red-600" />
						<ErrorBox message={error || "Le paiement n'a pas abouti."} />
						<p className="text-sm text-gray-500">Aucun montant n'a été encaissé. Le panier est conservé.</p>
						<button type="button" onClick={onCancel} className="w-full rounded-xl bg-[#34251D] p-3 font-semibold text-white">
							Retour au panier
						</button>
					</>
				) : null}

				{phase === "waiting" && error ? <ErrorBox message={error} /> : null}
			</div>
		</Modal>
	);
}

function SaleDoneModal({ sale, onClose }) {
	const isCash = sale.payment_method === "cash";
	return (
		<Modal title={`Vente n° ${sale.sale_number}`} onClose={onClose}>
			<div className="space-y-4 text-center">
				<CheckCircle2 size={56} className="mx-auto text-emerald-600" />
				<p className="text-lg font-semibold">
					Payé {isCash ? "en espèces" : "par terminal"} · {formatCHF(sale.total_amount)}
				</p>
				{isCash && sale.payment?.change ? (
					<div className="rounded-xl bg-emerald-50 p-4 text-emerald-900">
						<p className="text-sm">Monnaie à rendre</p>
						<p className="text-4xl font-bold">{formatCHF(sale.payment.change)}</p>
						<p className="text-xs">Reçu {formatCHF(sale.payment.cash_received)}</p>
					</div>
				) : null}
				{!isCash && sale.payment?.card_last4 ? (
					<p className="text-sm text-gray-500">
						{(sale.payment.card_brand || "Carte").toUpperCase()} •••• {sale.payment.card_last4}
					</p>
				) : null}
				<button
					type="button"
					onClick={onClose}
					autoFocus
					className="w-full rounded-xl bg-[#34251D] p-4 text-lg font-semibold text-white"
				>
					Nouvelle vente
				</button>
			</div>
		</Modal>
	);
}
